<template>
  <div class="content dashboard">
    <div class="dashboard__greeting">
      <h1>{{ greeting }}, {{ firstName }}</h1>
      <p class="dashboard__date">{{ todayLabel }}</p>
    </div>

    <div class="dashboard__stats">
      <div class="stat-tile">
        <div class="stat-tile__icon"><i class="icon-svg icon-file-text"></i></div>
        <div class="stat-tile__value">
          <span v-if="loading.docs">…</span>
          <span v-else>{{ formatNumber(stats.ownDocs) }}</span>
        </div>
        <div class="stat-tile__label">{{ $t('stat_own_docs') }}</div>
      </div>
      <!-- Everything within the user's reach: their own folders, or the whole tree for an
           administrator. It gets its own tile next to the previous one so both figures are
           read as the same measurement at two scopes, instead of one being a footnote. -->
      <div class="stat-tile">
        <div class="stat-tile__icon"><i class="icon-svg icon-folder"></i></div>
        <div class="stat-tile__value">
          <span v-if="loading.docs">…</span>
          <span v-else>{{ formatNumber(stats.allDocs) }}</span>
        </div>
        <div class="stat-tile__label">{{ $t('stat_all_docs') }}</div>
      </div>
      <div class="stat-tile">
        <div class="stat-tile__icon"><i class="icon-svg icon-user"></i></div>
        <div class="stat-tile__value">
          <span v-if="loading.users">…</span>
          <span v-else>{{ formatNumber(stats.totalUsers) }}</span>
        </div>
        <div class="stat-tile__label">{{ $t('stat_active_users') }}</div>
      </div>
      <div class="stat-tile">
        <div class="stat-tile__icon"><i class="icon-svg icon-building"></i></div>
        <div class="stat-tile__value">
          <span v-if="loading.companies">…</span>
          <span v-else>{{ formatNumber(stats.totalCompanies) }}</span>
        </div>
        <div class="stat-tile__label">{{ $t('listado_companias') }}</div>
      </div>
    </div>

    <div class="dashboard__grid-2">
      <!-- Both series share this block: same shape, same scale rules, so the only thing
           that changes between them is the scope of the figures they carry. Each keeps
           its own vertical scale, since the own series is a subset of the total one and
           a shared scale would flatten it to nothing. -->
      <div class="dashboard-card" v-for="chart in monthlyCharts" :key="chart.key">
        <div class="dashboard-card__head">
          <div>
            <p class="dashboard-card__title">{{ chart.title }}</p>
            <p class="dashboard-card__sub" v-if="!chart.loading && chart.peak">
              {{ $t('chart_peak') }}: {{ chart.peak.label }}, {{ formatNumber(chart.peak.value) }}
            </p>
          </div>
          <select class="dashboard-card__select" :value="chart.year"
                  @change="onYearChange(chart.key, $event.target.value)">
            <option value="">{{ $t('chart_all_years') }}</option>
            <option v-for="y in yearOptions" :key="y" :value="y">{{ y }}</option>
          </select>
        </div>
        <div v-if="chart.loading" class="dashboard-card__empty">{{ $t('loading') }}</div>
        <template v-else>
          <div class="barchart">
            <div v-for="(v, i) in chart.values" :key="i"
                 class="barchart__col" :class="{ 'barchart__col--peak': chart.max > 0 && v === chart.max }">
              <span class="barchart__tooltip">{{ monthLabels[i] }}: {{ formatNumber(v) }}</span>
              <div class="barchart__bar" :style="{ height: barHeight(v, chart.max) + '%' }"></div>
            </div>
          </div>
          <div class="barchart__labels">
            <span v-for="(m, i) in monthLabels" :key="i">{{ m }}</span>
          </div>
        </template>
      </div>
    </div>

    <!-- LOWER ROW -->
    <div class="dashboard__grid-2">
      <div class="dashboard-card">
        <div class="dashboard-card__head">
          <div>
            <p class="dashboard-card__title">{{ $t('quick_access') }}</p>
            <p class="dashboard-card__sub">{{ $t('quick_access_sub') }}</p>
          </div>
        </div>
        <div class="dashboard-list">
          <router-link class="dashboard-list__row dashboard-list__row--link" :to="isAdmin ? '/admin/documents' : '/admin/documentos'">
            <div class="dashboard-list__icon"><i class="icon-svg icon-file-text"></i></div>
            <div class="dashboard-list__body">
              <div class="dashboard-list__title">{{ $t('Documentos') }}</div>
              <div class="dashboard-list__sub">{{ $t('quick_access_docs') }}</div>
            </div>
          </router-link>
          <router-link class="dashboard-list__row dashboard-list__row--link" to="/admin/solicitudes_pdte">
            <div class="dashboard-list__icon"><i class="icon-svg icon-clock"></i></div>
            <div class="dashboard-list__body">
              <div class="dashboard-list__title">{{ $t('solicitudes_pendientes') }}</div>
              <div class="dashboard-list__sub">{{ $t('quick_access_requests') }}</div>
            </div>
          </router-link>
          <router-link class="dashboard-list__row dashboard-list__row--link" to="/admin/agenda">
            <div class="dashboard-list__icon"><i class="icon-svg icon-calendar"></i></div>
            <div class="dashboard-list__body">
              <div class="dashboard-list__title">{{ $t('mi_calendario') }}</div>
              <div class="dashboard-list__sub">{{ $t('quick_access_agenda') }}</div>
            </div>
          </router-link>
          <router-link class="dashboard-list__row dashboard-list__row--link" :to="isAdmin ? '/admin/contacts' : '/admin/carpetas'">
            <div class="dashboard-list__icon"><i class="icon-svg icon-building"></i></div>
            <div class="dashboard-list__body">
              <div class="dashboard-list__title">{{ isAdmin ? $t('listado_companias') : $t('folders') }}</div>
              <div class="dashboard-list__sub" v-if="isAdmin && !loading.companies">
                {{ $t('quick_access_companies', { count: stats.totalCompanies }) }}
              </div>
              <div class="dashboard-list__sub" v-else-if="!isAdmin">{{ $t('quick_access_docs') }}</div>
            </div>
          </router-link>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'

export default {
  name: 'HomePage',
  data () {
    return {
      loading: {
        docs: true,
        users: true,
        companies: true
      },
      stats: {
        ownDocs: 0,
        allDocs: 0,
        totalUsers: 0,
        totalCompanies: 0
      },
      // One entry per monthly chart. Each carries its own year, so the two can sit on
      // different periods, and reloading one never touches the other.
      series: {
        own: { values: [], year: String(new Date().getFullYear()), loading: true },
        all: { values: [], year: String(new Date().getFullYear()), loading: true }
      },
      years: [],
      monthLabels: ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic']
    }
  },
  computed: {
    ...mapGetters({
      user: 'login/user'
    }),
    isAdmin () {
      return !!(this.user && this.user.rol_id == 1)
    },
    firstName () {
      if (!this.user || !this.user.name) return ''
      return this.user.name.split(' ')[0]
    },
    greeting () {
      const h = new Date().getHours()
      if (h < 12) return this.$t('greeting_morning')
      if (h < 20) return this.$t('greeting_afternoon')
      return this.$t('greeting_evening')
    },
    currentYear () {
      return new Date().getFullYear()
    },
    yearOptions () {
      const current = String(this.currentYear)
      const years = this.years.map(String)
      return years.indexOf(current) === -1 ? [current].concat(years) : years
    },
    todayLabel () {
      return new Date().toLocaleDateString(this.$i18n.locale === 'en' ? 'en-GB' : 'es-ES', {
        weekday: 'long', day: 'numeric', month: 'long', year: 'numeric'
      })
    },
    // Both monthly charts are rendered from this list, so adding or reordering a series
    // is a change here rather than another copy of the card markup.
    monthlyCharts () {
      return [
        this.chartOf('own', this.$t('chart_own_docs_by_month')),
        this.chartOf('all', this.$t('chart_docs_by_month'))
      ]
    }
  },
  mounted () {
    this.loadDocumentCounts()
    this.loadUsers()
    this.loadCompanies()
    this.loadYears()
    this.loadSeries('own')
    this.loadSeries('all')
  },
  methods: {
    formatNumber (n) {
      return (n || 0).toLocaleString('es-ES')
    },
    chartOf (key, title) {
      const serie = this.series[key]
      return {
        key,
        title,
        values: serie.values,
        year: serie.year,
        max: this.maxOf(serie.values),
        peak: this.peakOf(serie.values),
        loading: serie.loading
      }
    },
    maxOf (values) {
      return values.length ? Math.max(...values) : 0
    },
    peakOf (values) {
      const max = this.maxOf(values)
      if (!max) return null
      return { label: this.monthLabels[values.indexOf(max)], value: max }
    },
    barHeight (v, max) {
      if (!max) return 3
      return Math.max(3, Math.round((v / max) * 100))
    },
    loadDocumentCounts () {
      // Folders are not counted here any more, and the figures are scoped to what the
      // user reaches: their own folders, or the whole tree for an administrator.
      this.$axios.get('/document/count')
        .then(response => {
          const data = response.data || {}
          this.stats.ownDocs = data.own || 0
          this.stats.allDocs = data.total || 0
        })
        .catch(() => {})
        .finally(() => { this.loading.docs = false })
    },
    loadUsers () {
      this.$axios.get('/user')
        .then(response => { this.stats.totalUsers = (response.data || []).length })
        .catch(() => {})
        .finally(() => { this.loading.users = false })
    },
    loadCompanies () {
      this.$axios.get('/contacts')
        .then(response => { this.stats.totalCompanies = (response.data || []).length })
        .catch(() => {})
        .finally(() => { this.loading.companies = false })
    },
    loadYears () {
      this.$axios.get('/chart_document_years')
        .then(response => { this.years = response.data || [] })
        .catch(() => {})
    },
    loadSeries (key) {
      const serie = this.series[key]
      const params = {}
      if (key === 'own') params.scope = 'own'
      if (serie.year) params.year = serie.year
      serie.loading = true
      this.$axios.get('/chart_document_reg', { params })
        .then(response => { serie.values = response.data || [] })
        .catch(() => {})
        .finally(() => { serie.loading = false })
    },
    onYearChange (key, year) {
      this.series[key].year = year
      this.loadSeries(key)
    }
  }
}
</script>

<style>
/* ---------- Página de inicio: dashboard ---------- */
.dashboard {
  padding-top: 32px;
  padding-bottom: 56px;
  box-sizing: border-box;
}
.dashboard__greeting {
  margin-bottom: 24px;
}
.dashboard__eyebrow {
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: #6b7280;
  margin: 0 0 6px;
}
.dashboard__greeting h1 {
  margin: 0;
  font-size: 24px;
  font-weight: 700;
  letter-spacing: -0.01em;
  color: #2a2f3c;
}
.dashboard__date {
  margin: 6px 0 0;
  color: #6b7280;
  font-size: 13px;
  text-transform: capitalize;
}

/* stat tiles */
.dashboard__stats {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 14px;
  margin-bottom: 20px;
}
.stat-tile {
  background: #fff;
  border: 1px solid var(--cd-border-light, #e2e5ec);
  border-radius: 14px;
  padding: 16px;
  box-shadow: 0 1px 2px rgba(16, 24, 40, 0.04), 0 1px 3px rgba(16, 24, 40, 0.06);
  display: flex;
  flex-direction: column;
  gap: 10px;
  min-width: 0;
}
.stat-tile__icon {
  width: 30px;
  height: 30px;
  border-radius: 8px;
  background: rgba(46, 158, 79, 0.12);
  display: flex;
  align-items: center;
  justify-content: center;
}
.stat-tile__icon .icon-svg {
  width: 16px !important;
  height: 16px;
  margin: 0 !important;
  color: var(--cd-brand-green, #2E9E4F);
}
.stat-tile__value {
  font-size: 22px;
  font-weight: 700;
  letter-spacing: -0.01em;
  line-height: 1;
  color: #2a2f3c;
}
.stat-tile__label {
  font-size: 12px;
  color: #6b7280;
  line-height: 1.3;
}

/* chart cards */
.dashboard__grid-2 {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 16px;
  margin-bottom: 16px;
}
.dashboard-card {
  background: #fff;
  border: 1px solid var(--cd-border-light, #e2e5ec);
  border-radius: 14px;
  padding: 20px;
  box-shadow: 0 1px 2px rgba(16, 24, 40, 0.04), 0 1px 3px rgba(16, 24, 40, 0.06);
}
.dashboard-card__head {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  margin-bottom: 18px;
  gap: 12px;
}
.dashboard-card__title {
  font-size: 14px;
  font-weight: 700;
  margin: 0;
  color: #2a2f3c;
}
.dashboard-card__sub {
  font-size: 12px;
  color: #6b7280;
  margin: 2px 0 0;
}
/* Year filter of a chart card: it takes the place the peak figure used to hold on the
   right, so the head keeps its height and the peak moves under the title. */
.dashboard-card__select {
  height: 28px;
  border: 1px solid var(--cd-border-light, #e2e5ec);
  border-radius: 8px;
  background: #fff;
  color: #4a4f5a;
  font-size: 12px;
  padding: 0 24px 0 8px;
  cursor: pointer;
  flex-shrink: 0;
  appearance: none;
  -webkit-appearance: none;
  background-image: url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='%236b7280' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'><path d='M6 9l6 6l6 -6'/></svg>");
  background-repeat: no-repeat;
  background-position: right 6px center;
}
.dashboard-card__select:focus {
  outline: none;
  border-color: var(--cd-brand-green, #2E9E4F);
}
.dashboard-card__meta {
  font-size: 12px;
  color: #6b7280;
  white-space: nowrap;
}
.dashboard-card__empty {
  font-size: 13px;
  color: #6b7280;
  padding: 24px 0;
  text-align: center;
}

/* bar chart */
.barchart {
  display: flex;
  align-items: flex-end;
  gap: 8px;
  height: 160px;
  padding-top: 8px;
  border-bottom: 1px solid var(--cd-border-light, #e2e5ec);
}
.barchart__col {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-end;
  height: 100%;
  position: relative;
}
.barchart__bar {
  width: 100%;
  max-width: 26px;
  border-radius: 4px 4px 0 0;
  background: rgba(46, 158, 79, 0.28);
  transition: background 0.15s ease;
}
.barchart__col:hover .barchart__bar,
.barchart__col--peak .barchart__bar {
  background: var(--cd-brand-green, #2E9E4F);
}
.barchart__tooltip {
  position: absolute;
  bottom: 100%;
  margin-bottom: 6px;
  background: #2a2f3c;
  color: #fff;
  font-size: 11px;
  font-weight: 600;
  padding: 3px 7px;
  border-radius: 6px;
  white-space: nowrap;
  opacity: 0;
  pointer-events: none;
  transition: opacity 0.1s ease;
}
.barchart__col:hover .barchart__tooltip,
.barchart__col--peak .barchart__tooltip {
  opacity: 1;
}
.barchart__labels {
  display: flex;
  gap: 8px;
  margin-top: 8px;
}
.barchart__labels span {
  flex: 1;
  text-align: center;
  font-size: 11px;
  color: #6b7280;
}

/* lists */
.dashboard-list {
  display: flex;
  flex-direction: column;
}
.dashboard-list__row {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 0;
  border-bottom: 1px solid var(--cd-border-light, #e2e5ec);
  text-decoration: none;
  color: inherit;
}
.dashboard-list__row:last-child { border-bottom: none; padding-bottom: 0; }
.dashboard-list__row:first-child { padding-top: 0; }
.dashboard-list__row--link {
  border-radius: 8px;
  margin: 0 -8px;
  padding-left: 8px;
  padding-right: 8px;
}
.dashboard-list__row--link:hover {
  background: rgba(46, 158, 79, 0.08);
}
.dashboard-list__icon {
  width: 30px;
  height: 30px;
  border-radius: 8px;
  background: #f7f7f8;
  border: 1px solid var(--cd-border-light, #e2e5ec);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
.dashboard-list__icon .icon-svg {
  width: 15px !important;
  height: 15px;
  margin: 0 !important;
  color: #6b7280;
}
.dashboard-list__body { min-width: 0; flex: 1; }
.dashboard-list__title {
  font-size: 13px;
  font-weight: 600;
  color: #2a2f3c;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.dashboard-list__sub {
  font-size: 11.5px;
  color: #6b7280;
}

@media (max-width: 900px) {
  .dashboard__stats { grid-template-columns: repeat(2, 1fr); }
  .dashboard__grid-2 { grid-template-columns: 1fr; }
}
@media (max-width: 520px) {
  .dashboard { padding-top: 20px; padding-bottom: 40px; }
  .dashboard__stats { grid-template-columns: repeat(2, 1fr); }
}
</style>
